import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Alert from "../../components/common/Alert";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Input from "../../components/common/Input";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Select from "../../components/common/Select";
import Table from "../../components/common/Table";
import {
  useGetOpenTillSessionsQuery,
  useGetPosOrdersQuery,
  useGetPosOrderPaymentsQuery,
  useGetTillSessionQuery,
  useLazyGetPosOrderPaymentsQuery,
  useUpdatePosPaymentMethodMutation,
} from "../../store/api";
import { useStore } from "../../context/StoreContext";
import useCurrentOperator from "../../hooks/useCurrentOperator";
import { useDeviceCashSessionResolution } from "../../hooks/useDeviceCashSession";
import { formatApiDateTime } from "../../utils/dateTime";
import { getPosErrorMessage } from "../../utils/posErrorHandler";

const PAYMENT_METHOD_OPTIONS = ["CASH", "CARD", "UPI", "OTHER"];
const PAYMENT_METHOD_FILTER_OPTIONS = ["CASH", "CARD"];

const formatMoney = (amount) => `$${Number(amount || 0).toFixed(2)}`;

const normalize = (value) => String(value ?? "").trim().toUpperCase();

const toStartOfDayIso = (value) => {
  if (!value) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  const date = new Date(year, month - 1, day, 0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const toEndOfDayIso = (value) => {
  if (!value) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  const date = new Date(year, month - 1, day, 23, 59, 59, 999);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.orders)) return value.orders;
  if (Array.isArray(value?.payments)) return value.payments;
  if (Array.isArray(value?.items)) return value.items;
  if (value && typeof value === "object") return [value];
  return [];
};

const getOrderDateValue = (order) =>
  order?.completedAt ||
  order?.closedAt ||
  order?.paidAt ||
  order?.updatedAt ||
  order?.createdAt ||
  order?.placedAt ||
  order?.orderDate ||
  null;

const getPaymentDateValue = (payment) =>
  payment?.capturedAt || payment?.updatedAt || payment?.createdAt || null;

const getOrderSessionValue = (order) =>
  order?.cashSessionId ||
  order?.sessionId ||
  order?.tillSessionId ||
  order?.shiftSessionId ||
  order?.tillSession?.cashSessionId ||
  order?.tillSession?.sessionId ||
  null;

const matchesSessionContext = (order, session) => {
  if (!session) return true;

  const sessionId = String(session.cashSessionId || session.sessionId || "");
  const orderSessionId = String(getOrderSessionValue(order) || "");
  if (sessionId && orderSessionId && orderSessionId === sessionId) {
    return true;
  }

  const sessionTillId = String(session.tillId || "");
  const orderTillId = String(
    order?.tillId || order?.till?.tillId || order?.tillSession?.tillId || "",
  );
  if (sessionTillId && orderTillId && orderTillId !== sessionTillId) {
    return false;
  }

  const sessionBusinessDate = String(session.businessDate || "");
  const orderBusinessDate = String(
    order?.businessDate || order?.orderBusinessDate || order?.shiftBusinessDate || "",
  );
  if (sessionBusinessDate && orderBusinessDate && orderBusinessDate !== sessionBusinessDate) {
    return false;
  }

  return Boolean(orderSessionId || orderTillId || orderBusinessDate);
};

const pickLatestPayment = (payments) =>
  asArray(payments)
    .slice()
    .sort((a, b) => {
      const left = new Date(getPaymentDateValue(a) || 0).getTime();
      const right = new Date(getPaymentDateValue(b) || 0).getTime();
      return right - left;
    })[0] || null;

const isEditablePayment = (payment) => {
  const status = normalize(payment?.paymentStatus || payment?.status);
  const provider = normalize(payment?.provider);
  return status === "CAPTURED" && (!provider || provider === "POS");
};

const formatOrderType = (value) => {
  const normalized = normalize(value);
  if (normalized === "DINEIN" || normalized === "TABLE_DINEIN" || normalized === "TABLE_DINE_IN") {
    return "Dine In";
  }
  if (normalized === "TAKEOUT") return "Takeaway";
  if (normalized === "DELIVERY") return "Delivery";
  if (!normalized) return "-";
  return normalized;
};

const orderTypeChipClasses = (value) => {
  const normalized = normalize(value);
  if (normalized === "DINEIN" || normalized === "TABLE_DINEIN" || normalized === "TABLE_DINE_IN") {
    return "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300";
  }
  if (normalized === "TAKEOUT") {
    return "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300";
  }
  if (normalized === "DELIVERY") {
    return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200";
  }
  return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
};

const paymentMethodChipClasses = (value) => {
  const normalized = normalize(value);
  if (normalized === "CASH") {
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300";
  }
  if (normalized === "CARD") {
    return "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300";
  }
  if (normalized === "UPI") {
    return "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300";
  }
  if (normalized === "OTHER") {
    return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200";
  }
  return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
};

const TEXT_INPUT_CLASSES = [
  "h-10 w-full min-w-0 rounded-lg border px-3 text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 shadow-sm",
  "outline-none transition",
  "border-slate-200 dark:border-slate-600 focus:border-slate-400 dark:focus:border-slate-500",
].join(" ");

const ChangePaymentMethod = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedStoreId } = useStore();
  const { operatorName } = useCurrentOperator();
  const [successMessage, setSuccessMessage] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    till: "",
    currentPaymentMethod: "",
  });
  const [rowDrafts, setRowDrafts] = useState({});
  const [updatedRows, setUpdatedRows] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [paymentAuditByOrderId, setPaymentAuditByOrderId] = useState({});
  const [confirmedMethodByOrderId, setConfirmedMethodByOrderId] = useState({});
  const [editingOrder, setEditingOrder] = useState(null);
  const [modalError, setModalError] = useState("");

  const requestedSessionId = searchParams.get("sessionId") || "";

  const { data: openSessionsData } = useGetOpenTillSessionsQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });

  const openSessions = useMemo(
    () => (Array.isArray(openSessionsData) ? openSessionsData : []),
    [openSessionsData],
  );

  const deviceSessionResolution = useDeviceCashSessionResolution(
    openSessions,
    Boolean(selectedStoreId),
  );

  const selectedSessionId = useMemo(() => {
    if (requestedSessionId) return requestedSessionId;
    if (deviceSessionResolution.status !== "ready") return "";
    return deviceSessionResolution.selectedSessionId || "";
  }, [
    deviceSessionResolution.selectedSessionId,
    deviceSessionResolution.status,
    requestedSessionId,
  ]);

  const { data: sessionData } = useGetTillSessionQuery(selectedSessionId, {
    skip: !selectedSessionId,
  });

  const session = sessionData && typeof sessionData === "object" ? sessionData : null;

  const queryArgs = useMemo(
    () => ({
      page: 1,
      pageSize: 200,
      from: toStartOfDayIso(filters.fromDate),
      to: toEndOfDayIso(filters.toDate),
      search: appliedSearch.trim() || undefined,
    }),
    [appliedSearch, filters.fromDate, filters.toDate],
  );

  const {
    data,
    isLoading,
    isError,
    error,
    refetch: refetchOrders,
  } = useGetPosOrdersQuery(queryArgs, {
    skip: !selectedStoreId,
  });

  const orders = useMemo(() => asArray(data), [data]);

  const sessionScopedOrders = useMemo(
    () => orders.filter((order) => matchesSessionContext(order, session)),
    [orders, session],
  );

  const tillOptions = useMemo(() => {
    const values = new Set();
    sessionScopedOrders.forEach((order) => {
      const label =
        order?.tillName || order?.tillCode || order?.till || order?.tillId;
      if (label) values.add(String(label));
    });
    return ["All Tills", ...Array.from(values).sort((a, b) => a.localeCompare(b))];
  }, [sessionScopedOrders]);

  const filteredOrders = useMemo(() => {
    return sessionScopedOrders.filter((order) => {
      const tillLabel = String(
        order?.tillName || order?.tillCode || order?.till || order?.tillId || "",
      );
      const currentMethod = normalize(
        order?.currentPaymentMethod ||
          order?.paymentMethod ||
          order?.tenderMethod ||
          order?.paymentStatus ||
          "",
      );
      const dateValue = getOrderDateValue(order);
      const dateMs = dateValue ? new Date(dateValue).getTime() : NaN;
      const fromMs = filters.fromDate ? new Date(filters.fromDate).getTime() : NaN;
      const toMs = filters.toDate ? new Date(filters.toDate).getTime() : NaN;

      if (filters.till && filters.till !== "All Tills" && tillLabel !== filters.till) {
        return false;
      }
      if (
        filters.currentPaymentMethod &&
        normalize(filters.currentPaymentMethod) !== currentMethod
      ) {
        return false;
      }
      if (!Number.isNaN(fromMs) && !Number.isNaN(dateMs) && dateMs < fromMs) {
        return false;
      }
      if (!Number.isNaN(toMs) && !Number.isNaN(dateMs) && dateMs > toMs) {
        return false;
      }
      return true;
    });
  }, [filters, sessionScopedOrders]);

  const [loadOrderPayments] = useLazyGetPosOrderPaymentsQuery();

  useEffect(() => {
    setRowDrafts((prev) => {
      const next = {};
      filteredOrders.forEach((order) => {
        const orderId = order?.orderId;
        if (!orderId) return;
        const currentMethod = normalize(
          order?.currentPaymentMethod ||
            order?.paymentMethod ||
            order?.tenderMethod ||
            "CASH",
        );
        const paymentAudit = paymentAuditByOrderId[orderId] || null;
        const persistedReason = String(
          paymentAudit?.changeReason || paymentAudit?.notes || "",
        ).trim();
        next[orderId] = prev[orderId] || {
          newMethod: currentMethod,
          reasonNotes: persistedReason,
        };
        if (!String(next[orderId]?.reasonNotes || "").trim() && persistedReason) {
          next[orderId] = {
            ...next[orderId],
            reasonNotes: persistedReason,
          };
        }
      });
      return next;
    });
  }, [filteredOrders, paymentAuditByOrderId]);

  useEffect(() => {
    let isCancelled = false;
    const orderIds = filteredOrders
      .map((order) => order?.orderId)
      .filter(Boolean);

    if (!orderIds.length) {
      setPaymentAuditByOrderId({});
      return undefined;
    }

    const loadAudits = async () => {
      const results = await Promise.all(
        orderIds.map(async (orderId) => {
          try {
            const paymentsData = await loadOrderPayments(orderId).unwrap();
            return [orderId, pickLatestPayment(paymentsData)];
          } catch {
            return [orderId, null];
          }
        }),
      );

      if (isCancelled) return;

      setPaymentAuditByOrderId((prev) => {
        const next = { ...prev };
        results.forEach(([orderId, paymentAudit]) => {
          if (!orderId) return;
          next[orderId] = paymentAudit;
        });
        return next;
      });
    };

    loadAudits();

    return () => {
      isCancelled = true;
    };
  }, [filteredOrders, loadOrderPayments]);

  const {
    data: editingPaymentsData,
    refetch: refetchEditingPayments,
  } = useGetPosOrderPaymentsQuery(editingOrder?.orderId, {
    skip: !editingOrder?.orderId,
  });

  const editingPayments = useMemo(
    () =>
      asArray(editingPaymentsData).slice().sort((a, b) => {
        const left = new Date(getPaymentDateValue(a) || 0).getTime();
        const right = new Date(getPaymentDateValue(b) || 0).getTime();
        return right - left;
      }),
    [editingPaymentsData],
  );

  const editablePayment = useMemo(() => {
    if (!editingPayments.length) return null;
    return editingPayments.find(isEditablePayment) || editingPayments[0] || null;
  }, [editingPayments]);

  const [updatePaymentMethod, { isLoading: isUpdatingPaymentMethod }] =
    useUpdatePosPaymentMethodMutation();

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applySearch = () => {
    setAppliedSearch(searchDraft.trim());
  };

  const resetFilters = () => {
    setSearchDraft("");
    setAppliedSearch("");
    setFilters({
      fromDate: "",
      toDate: "",
      till: "",
      currentPaymentMethod: "",
    });
  };

  const openOrderEditor = (order) => {
    setEditingOrder(order);
    setModalError("");
  };

  const setRowError = (orderId, field = "", message = "") => {
    if (!orderId) return;
    setRowErrors((prev) => ({
      ...prev,
      [orderId]: { field, message },
    }));
  };

  const clearRowError = (orderId) => {
    if (!orderId) return;
    setRowErrors((prev) => {
      if (!prev[orderId]) return prev;
      const next = { ...prev };
      delete next[orderId];
      return next;
    });
  };

  const closeOrderEditor = () => {
    if (isUpdatingPaymentMethod) return;
    setEditingOrder(null);
    setModalError("");
  };

  const getOrderCurrentMethod = (order) =>
    normalize(
      confirmedMethodByOrderId[order?.orderId] ||
        order?.currentPaymentMethod ||
        order?.paymentMethod ||
        order?.tenderMethod ||
        order?.paymentStatus ||
        "CASH",
    );

  const getOrderDraft = (orderId, currentMethod) =>
    rowDrafts[orderId] || {
      newMethod: currentMethod,
      reasonNotes: "",
    };

  const submitOrderChange = async () => {
    if (!editingOrder) return;

    const targetPayment = editablePayment;
    const currentMethod = getOrderCurrentMethod(editingOrder);
    const draft = getOrderDraft(editingOrder.orderId, currentMethod);
    const requestedMethod = normalize(draft.newMethod);
    const reason = String(draft.reasonNotes || "").trim();

    if (!targetPayment?.paymentId) {
      setModalError("No captured payment was found for this order.");
      return;
    }

    if (!requestedMethod) {
      setModalError("Please select a new payment method.");
      return;
    }

    if (!reason) {
      setModalError("Reason for change is required.");
      return;
    }

    setModalError("");

    try {
      const response = await updatePaymentMethod({
        paymentId: targetPayment.paymentId,
        payload: {
          paymentMethod: requestedMethod,
          reason,
          source: "POS",
        },
      }).unwrap();

      setSuccessMessage(response?.message || "Payment method updated successfully.");
      setConfirmedMethodByOrderId((prev) => ({
        ...prev,
        [editingOrder.orderId]: requestedMethod,
      }));
      setUpdatedRows((prev) => ({
        ...prev,
        [editingOrder.orderId]: {
          changedBy: response?.updatedBy || operatorName || "Operator",
          changedAt: response?.updatedAt || new Date().toISOString(),
        },
      }));
      closeOrderEditor();
      await Promise.all([refetchOrders(), refetchEditingPayments()]);
    } catch (err) {
      setModalError(getPosErrorMessage(err));
    }
  };

  const orderColumns = [
    { key: "orderNo", label: "Order No", headerClassName: "w-[9%]" },
    { key: "date", label: "Date & Time", headerClassName: "w-[11%]" },
    { key: "till", label: "Till", headerClassName: "w-[6%]" },
    { key: "orderType", label: "Order Type", headerClassName: "w-[9%]" },
    { key: "total", label: "Total Amount", headerClassName: "w-[8%]" },
    {
      key: "currentMethod",
      label: "Current Payment Method",
      headerClassName: "w-[10%]",
    },
    { key: "newMethod", label: "New Payment Method", headerClassName: "w-[11%]" },
    {
      key: "reason",
      label: "Reason / Notes *",
      headerClassName: "w-[17%]",
      cellClassName: "min-w-0",
    },
    { key: "actions", label: "Actions", headerClassName: "w-[7%]" },
  ];

  const orderRows = filteredOrders.map((order) => {
    const orderId = order?.orderId;
    const currentMethod = getOrderCurrentMethod(order);
    const draft = getOrderDraft(orderId, currentMethod);
    const updateState = updatedRows[orderId];
    const paymentAudit = paymentAuditByOrderId[orderId] || null;
    const rowChanged = normalize(draft.newMethod) !== currentMethod;
    const orderNo = order?.orderNo || order?.orderNumber || orderId || "-";
    const hasPersistedAudit = Boolean(
      paymentAudit &&
        (
          paymentAudit.updatedBy ||
          paymentAudit.changeReason ||
          paymentAudit.notes ||
          (paymentAudit.updatedAt &&
            paymentAudit.createdAt &&
            paymentAudit.updatedAt !== paymentAudit.createdAt)
        ),
    );
    const rowReasonNotes = String(
      draft.reasonNotes ||
        paymentAudit?.changeReason ||
        paymentAudit?.notes ||
        "",
    ).trim();
    const rowError = rowErrors[orderId] || null;

    return {
      id: orderId,
      className: (updateState || hasPersistedAudit)
        ? "bg-emerald-50/60 dark:bg-emerald-950/20"
        : rowChanged
          ? "bg-amber-50/60 dark:bg-amber-950/20"
          : undefined,
      orderNo: (
        <span className="font-semibold text-slate-900 dark:text-slate-100">
          {orderNo}
        </span>
      ),
      date: formatApiDateTime(getOrderDateValue(order)),
      till:
        order?.tillName || order?.tillCode || order?.till || order?.tillId || "-",
      orderType: (
        <span
          className={[
            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold",
            orderTypeChipClasses(order?.orderType),
          ].join(" ")}
        >
          {formatOrderType(order?.orderType)}
        </span>
      ),
      total: (
        <span className="font-semibold text-slate-900 dark:text-slate-100">
          {formatMoney(order?.grandTotal ?? order?.totals?.grandTotal ?? 0)}
        </span>
      ),
      currentMethod: (
        <span
          className={[
            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold",
            paymentMethodChipClasses(currentMethod),
          ].join(" ")}
        >
          {currentMethod}
        </span>
      ),
      newMethod: (
        <div className="space-y-1.5">
          <Select
            value={draft.newMethod}
            onChange={(e) => {
              clearRowError(orderId);
              setRowDrafts((prev) => ({
                ...prev,
                [orderId]: {
                  ...(prev[orderId] || {}),
                  newMethod: e.target.value,
                },
              }));
            }}
            className={[
              "h-10 w-full min-w-0 max-w-[126px]",
              rowError?.field === "newMethod"
                ? "border-rose-300 focus:border-rose-400 dark:border-rose-600 dark:focus:border-rose-500"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
            error={rowError?.field === "newMethod"}
            aria-invalid={rowError?.field === "newMethod"}
          >
            {PAYMENT_METHOD_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
          {rowError?.field === "newMethod" && (
            <p className="text-xs font-medium text-rose-600 dark:text-rose-400">
              {rowError.message}
            </p>
          )}
        </div>
      ),
      reason: (
        <div className="space-y-1.5">
          <input
            value={rowReasonNotes}
            onChange={(e) => {
              clearRowError(orderId);
              setRowDrafts((prev) => ({
                ...prev,
                [orderId]: {
                  ...(prev[orderId] || {}),
                  reasonNotes: e.target.value,
                },
              }));
            }}
            className={[
              TEXT_INPUT_CLASSES,
              rowError?.field === "reasonNotes"
                ? "border-rose-300 focus:border-rose-400 dark:border-rose-600 dark:focus:border-rose-500"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
            placeholder="Cashier selected wrong tender"
            aria-label="Reason / Notes"
            aria-invalid={rowError?.field === "reasonNotes"}
          />
          {rowError?.field === "reasonNotes" && (
            <p className="text-xs font-medium text-rose-600 dark:text-rose-400">
              {rowError.message}
            </p>
          )}
        </div>
      ),
      actions: (
        <div className="flex items-center justify-end gap-2">
          <Button
            size="sm"
            variant="pos-primary"
            onClick={() => {
              if (!orderId) return;

              if (!rowChanged || !rowReasonNotes) {
                setRowError(
                  orderId,
                  !rowChanged ? "newMethod" : "reasonNotes",
                  !rowChanged
                    ? "Choose a new payment method before updating."
                    : "Reason / Notes is required before updating.",
                );
                return;
              }

              clearRowError(orderId);
              openOrderEditor(order);
            }}
            disabled={!orderId || isUpdatingPaymentMethod || updateState || hasPersistedAudit}
            title={
              rowError?.message ||
              (updateState || hasPersistedAudit
                ? "This row has already been updated."
                : undefined)
            }
          >
            Update
          </Button>
        </div>
      ),
    };
  });

  return (
    <div className="space-y-4 px-6 py-6">
      <PageHeader
        title="Change Payment Method"
        subtitle="Review orders in one table and correct captured tenders inline."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => navigate("/pos/cash-up")}>
              Back to Cash Up
            </Button>
          </div>
        }
      />

      {successMessage && <Alert tone="success">{successMessage}</Alert>}
      {!selectedStoreId && <Alert tone="warning">No store is selected.</Alert>}
      {selectedStoreId &&
        !requestedSessionId &&
        !deviceSessionResolution.isLoading &&
        deviceSessionResolution.status !== "ready" && (
          <Alert tone="warning">{deviceSessionResolution.message}</Alert>
        )}
      {selectedStoreId && !selectedSessionId && requestedSessionId && (
        <Alert tone="warning">
          No active till session was found. Open a till session first to review its payments.
        </Alert>
      )}
      {isError && (
        <Alert tone="error">
          {error?.data?.detail || error?.data?.message || "Failed to load orders."}
        </Alert>
      )}

      <Card className="space-y-4 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Input
            label="Search Orders"
            placeholder="Order no, customer, table..."
            className="w-[280px]"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applySearch();
              }
            }}
          />
          <Input
            label="From Date"
            type="date"
            className="w-[180px]"
            value={filters.fromDate}
            onChange={(e) => setFilter("fromDate", e.target.value)}
          />
          <Input
            label="To Date"
            type="date"
            className="w-[180px]"
            value={filters.toDate}
            onChange={(e) => setFilter("toDate", e.target.value)}
          />
          <Select
            label="Till"
            className="w-[220px]"
            value={filters.till}
            onChange={(e) => setFilter("till", e.target.value)}
          >
            <option value="">All Tills</option>
            {tillOptions
              .filter((option) => option !== "All Tills")
              .map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
          </Select>
          <Select
            label="Current Payment Method"
            className="w-[220px]"
            value={filters.currentPaymentMethod}
            onChange={(e) => setFilter("currentPaymentMethod", e.target.value)}
          >
            <option value="">All Methods</option>
            {PAYMENT_METHOD_FILTER_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
          <div className="flex shrink-0 items-center gap-2">
            <Button size="sm" variant="secondary" onClick={applySearch}>
              Search
            </Button>
            <Button size="sm" variant="secondary" onClick={resetFilters}>
              Reset
            </Button>
          </div>
          <div className="ml-auto shrink-0 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-sm font-semibold text-indigo-700">
            {filteredOrders.length} orders found
          </div>
        </div>

        {isLoading ? (
          <div className="p-4 text-sm text-slate-500">Loading orders...</div>
        ) : (
          <Table
            columns={orderColumns}
            rows={orderRows}
            emptyText="No placed orders found for the selected filters."
            tableClassName="table-fixed"
          />
        )}
      </Card>

      <Modal
        isOpen={Boolean(editingOrder)}
        onClose={closeOrderEditor}
        title="Confirm Payment Method Change"
        confirmText={isUpdatingPaymentMethod ? "Updating..." : "Confirm Update"}
        onConfirm={submitOrderChange}
        isLoading={isUpdatingPaymentMethod}
        size="lg"
        cancelText="Cancel"
        showCancel
        showCloseButton
      >
        {editingOrder && (
          <div className="space-y-4">
            {(() => {
              const currentMethod = getOrderCurrentMethod(editingOrder);
              const draft = getOrderDraft(editingOrder.orderId, currentMethod);
              const requestedMethod = normalize(draft.newMethod) || currentMethod;
              const paymentAudit = paymentAuditByOrderId[editingOrder.orderId] || null;
              const reason = String(
                draft.reasonNotes ||
                  paymentAudit?.changeReason ||
                  paymentAudit?.notes ||
                  "",
              ).trim();

              return (
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Order No
                    </span>
                    <div className="mt-1 text-base font-semibold text-slate-900 dark:text-slate-100">
                      {editingOrder?.orderNo ||
                        editingOrder?.orderNumber ||
                        editingOrder?.orderId ||
                        "-"}
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Till
                    </span>
                    <div className="mt-1 text-base font-semibold text-slate-900 dark:text-slate-100">
                      {editingOrder?.tillName ||
                        editingOrder?.tillCode ||
                        editingOrder?.till ||
                        editingOrder?.tillId ||
                        "-"}
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-300">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Current Method
                    </span>
                    <div
                      className={[
                        "mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold",
                        paymentMethodChipClasses(currentMethod),
                      ].join(" ")}
                    >
                      {currentMethod}
                    </div>
                  </div>

                  <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-slate-700 dark:border-indigo-800 dark:bg-indigo-950/30 dark:text-slate-300">
                    <span className="text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
                      New Method
                    </span>
                    <div
                      className={[
                        "mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold",
                        paymentMethodChipClasses(requestedMethod),
                      ].join(" ")}
                    >
                      {requestedMethod}
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-300 md:col-span-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Reason / Notes
                    </span>
                    <p className="mt-1 text-base text-slate-900 dark:text-slate-100">
                      {reason || "No reason provided."}
                    </p>
                  </div>

                </div>
              );
            })()}

            {modalError && <Alert tone="error">{modalError}</Alert>}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ChangePaymentMethod;
